import moment from 'moment'
import { isFlightSavedItemData } from 'tripPlanner/api/types'
import type { TripItineraryData, TripItineraryItem, TripItemWithoutDay, TripDay, TripStop, TransportOption } from 'tripPlanner/api/types'
import type { HotelSavedBooking } from 'tripPlanner/types/savedItemData'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { isRecord } from 'lib/object/objectUtils'
import { formatShortDateRange } from 'tripPlanner/utils'
import { hasValidLatLng } from 'tripPlanner/utils/mapMarkerTransforms'
import { getDayLabel } from './chatDates'
import { isCruiseSavedData, isExperienceSavedData, isHotelSavedData, type HotelSavedData } from './savedItemDataGuards'
import { resolveStopDuration } from './stopDuration'

export type TripHotelItem = TripItineraryItem | TripItemWithoutDay
export type SavedTripHotelItem = TripHotelItem & { savedItemData: HotelSavedData }

export type TripExperienceItem = TripItineraryItem | TripItemWithoutDay

export function isTripHotelItem(item: TripItineraryItem | TripItemWithoutDay): item is TripHotelItem {
  return item.type === 'ACCOMMODATION'
}

export function isSavedTripHotelItem(item: TripItineraryItem | TripItemWithoutDay): item is SavedTripHotelItem {
  return isTripHotelItem(item) && isHotelSavedData(item.savedItemData)
}

export function getStopHotelItems(stop: TripStop | undefined): Array<TripHotelItem> {
  const itemsById = new Map<number, TripHotelItem>()

  stop?.itemsWithoutDay?.forEach((item) => {
    if (isTripHotelItem(item)) {
      itemsById.set(item.id, item)
    }
  })

  stop?.days?.forEach((day) => {
    day.items.forEach((item) => {
      if (isTripHotelItem(item)) {
        itemsById.set(item.id, item)
      }
    })
  })

  return Array.from(itemsById.values())
}

/** Interpret the legacy API fields once; the rest of the UI uses booking booleans. */
type BookingStateInput = {
  isBooked?: boolean
  status?: TripItemWithoutDay['status']
}

export function getItemBookingState(item: BookingStateInput) {
  return {
    isLuxuryEscapesBooking: item.isBooked === true,
    isBooked: item.isBooked === true || isManuallyBookedItem(item),
  }
}

export function isManuallyBookedItem(item: BookingStateInput): boolean {
  return item.status === 'BOOKED'
}

export function isBookedItineraryItem(item: BookingStateInput): boolean {
  return getItemBookingState(item).isBooked
}

/** A displayed return journey represents both legs, including legacy one-leg markings. */
export function getTransportBookingState(option: TransportOption) {
  const legs = option.returnLeg ? [option, option.returnLeg] : [option]
  const isLuxuryEscapesBooking = legs.some(leg => (
    ('isBooked' in leg && leg.isBooked === true) ||
    (isFlightSavedItemData(leg.savedItemData) && leg.savedItemData.isBooked === true)
  ))
  return {
    isLuxuryEscapesBooking,
    isBooked: isLuxuryEscapesBooking || legs.some(isManuallyBookedItem),
  }
}

export function isBookedHotelItem(item: TripHotelItem): boolean {
  return item.type === 'ACCOMMODATION' && isBookedItineraryItem(item)
}

export function hasStopSavedOrBookedHotels(stop: TripStop | undefined): boolean {
  return getStopHotelItems(stop).some(item => isSavedTripHotelItem(item) || isBookedHotelItem(item))
}

export function getSavedHotelItemsFromStops(stops: TripItineraryData['stops'] | undefined): Array<SavedTripHotelItem> {
  const savedItemsByItemId = new Map<number, SavedTripHotelItem>()

  stops?.forEach((stop) => {
    getStopHotelItems(stop).forEach((item) => {
      if (isSavedTripHotelItem(item)) {
        savedItemsByItemId.set(item.id, item)
      }
    })
  })

  return Array.from(savedItemsByItemId.values())
}

export function isTripExperienceItem(item: TripItineraryItem | TripItemWithoutDay): item is TripExperienceItem {
  return item.type === 'EXPERIENCE'
}

export function getStopExperienceItems(stop: TripStop | undefined): Array<TripExperienceItem> {
  const itemsById = new Map<number, TripExperienceItem>()

  stop?.itemsWithoutDay?.forEach((item) => {
    if (isTripExperienceItem(item)) {
      itemsById.set(item.id, item)
    }
  })

  stop?.days?.forEach((day) => {
    day.items.forEach((item) => {
      if (isTripExperienceItem(item)) {
        itemsById.set(item.id, item)
      }
    })
  })

  return Array.from(itemsById.values())
}

// Booked experiences can come from orders (isBooked) or carry an explicit BOOKED status.
export function isBookedExperienceItem(item: TripExperienceItem): boolean {
  return isBookedItineraryItem(item)
}

export function hasStopSavedOrBookedExperiences(stop: TripStop | undefined): boolean {
  return getStopExperienceItems(stop).some(item => isExperienceSavedData(item.savedItemData) || isBookedExperienceItem(item))
}

// An experience's offer identity lives in savedItemData.experienceId when saved,
// or in `code` for order-created booked experiences that have no savedItemData.
export function getExperienceItemOfferId(item: TripExperienceItem): string | undefined {
  if (isExperienceSavedData(item.savedItemData)) return item.savedItemData.experienceId
  return getOfferCode(item)
}

export function getSavedExperienceOfferIds(stop: TripStop | undefined): Set<string> {
  const savedOfferIds = new Set<string>()

  getStopExperienceItems(stop).forEach((item) => {
    if (isExperienceSavedData(item.savedItemData)) {
      savedOfferIds.add(item.savedItemData.experienceId)
    }
  })

  return savedOfferIds
}

// offerId is a generic identifier: hotels store it as savedItemData.offerId,
// experiences store it as savedItemData.experienceId under a different source type.
function isCruiseSavedDataMatch(savedItemData: unknown, offerId: string): boolean {
  return isCruiseSavedData(savedItemData) && savedItemData.offerId === offerId
}

function getOfferCode(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined
  return typeof value.code === 'string' ? value.code : undefined
}

function hasOfferCode(value: unknown, offerId: string): boolean {
  return getOfferCode(value) === offerId
}

function hasCruiseType(value: unknown): boolean {
  return isRecord(value) && value.type === 'CRUISE'
}

function itemMatchesOffer(item: TripItineraryItem, offerId: string): boolean {
  if (hasCruiseType(item) && hasOfferCode(item, offerId)) return true
  // Order-created LE Experience items carry their offer identity in `code` and
  // may have no savedItemData, so match on the code before the savedItemData guard.
  if (isTripExperienceItem(item) && hasOfferCode(item, offerId)) return true
  if (!item.savedItemData) return false
  const { savedItemData } = item
  if (isCruiseSavedDataMatch(savedItemData, offerId)) return true
  if (isHotelSavedData(savedItemData)) return savedItemData.offerId === offerId
  if (isExperienceSavedData(savedItemData)) return savedItemData.experienceId === offerId
  return false
}

function transportOptionMatchesOffer(option: TransportOption, offerId: string): boolean {
  if (hasOfferCode(option, offerId)) return true
  return isCruiseSavedDataMatch(option.savedItemData, offerId)
}

function hasOfferTransport(stop: TripStop, offerId: string): boolean {
  return stop.transports?.some(transport =>
    transport.options.some(option => transportOptionMatchesOffer(option, offerId)),
  ) ?? false
}

interface CruiseDepartureSelection {
  itemId?: number
  departureId?: string
  departureDate?: string
}

function getCruiseDepartureSelectionFromSavedData(
  savedItemData: unknown,
  itemId?: number,
): CruiseDepartureSelection | undefined {
  if (!isCruiseSavedData(savedItemData) || !isRecord(savedItemData.departure)) return undefined

  const departure = savedItemData.departure
  const departureId = typeof departure.id === 'string' ? departure.id : undefined
  const departureDate = typeof departure.departureDate === 'string' ? departure.departureDate : undefined

  if (!departureId && !departureDate) return undefined

  return { itemId, departureId, departureDate }
}

export function findOfferItemInItinerary(
  offerId: string,
  itineraryData: TripItineraryData,
): TripItineraryItem | undefined {
  const allItems = itineraryData.stops.flatMap(stop => [
    ...stop.days.flatMap(day => day.items),
    ...(stop.itemsWithoutDay ?? []),
  ])
  return allItems.find(item => itemMatchesOffer(item, offerId))
}

export function getCruiseDepartureSelectionInItinerary(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): CruiseDepartureSelection | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  for (const stop of itineraryData.stops) {
    for (const day of stop.days) {
      const item = day.items.find(dayItem => itemMatchesOffer(dayItem, offerId))
      const selection = getCruiseDepartureSelectionFromSavedData(item?.savedItemData, item?.id)
      if (selection) return selection
    }

    const item = stop.itemsWithoutDay?.find(stopItem => itemMatchesOffer(stopItem, offerId))
    const itemSelection = getCruiseDepartureSelectionFromSavedData(item?.savedItemData, item?.id)
    if (itemSelection) return itemSelection

    const transportOption = stop.transports
      ?.flatMap(transport => transport.options)
      .find(option => transportOptionMatchesOffer(option, offerId))
    const transportSelection = getCruiseDepartureSelectionFromSavedData(
      transportOption?.savedItemData,
      transportOption?.itemId,
    )
    if (transportSelection) return transportSelection
  }

  return undefined
}

export function isOfferBookedInItinerary(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): boolean {
  if (!offerId || !itineraryData?.stops) return false
  const item = findOfferItemInItinerary(offerId, itineraryData)
  if (!item) return false

  return isBookedItineraryItem(item)
}

export function getOfferOrderId(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): string | undefined {
  if (!offerId || !itineraryData?.stops) return undefined
  const item = findOfferItemInItinerary(offerId, itineraryData)
  return item?.confirmationCode
}

export function getOfferBookedRooms(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): Array<DrawerHotelBookedRoom> | undefined {
  if (!offerId || !itineraryData?.stops) return undefined
  const item = findOfferItemInItinerary(offerId, itineraryData)
  if (!item?.isBooked || !item.confirmationCode || !item.savedItemData) return undefined

  const { savedItemData } = item
  if (!isHotelSavedData(savedItemData) || !savedItemData.bookedRooms?.length) return undefined

  return savedItemData.bookedRooms.map((room) => ({
    roomTypeName: room.roomTypeName,
    packageName: room.packageName,
    roomImageIds: room.roomImageIds,
    checkIn: savedItemData.checkIn ?? '',
    checkOut: savedItemData.checkOut ?? '',
    orderId: item.confirmationCode!,
    bookingNumber: room.bookingNumber,
  }))
}

export function getOfferSavedBooking(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): HotelSavedBooking | undefined {
  if (!offerId || !itineraryData?.stops) return undefined
  const item = findOfferItemInItinerary(offerId, itineraryData)
  if (!item?.savedItemData || !isHotelSavedData(item.savedItemData)) return undefined
  const { checkIn, checkOut, occupancies, packageId, roomTypeId, roomRateId, charterFlightBundle } = item.savedItemData
  if (!checkIn && !checkOut && !occupancies?.length && !packageId && !roomTypeId && !roomRateId) return undefined
  return {
    checkIn,
    checkOut,
    occupancies,
    packageId,
    roomTypeId,
    // The chat itinerary payload names this `roomRateId`; HotelSavedBooking (and the
    // drawer's `selectedRateId` prop) call it `rateId`. Map at the boundary — the two
    // saved-data contracts genuinely differ, see TRP-4253b.
    rateId: roomRateId,
    charterOriginAirportCode: charterFlightBundle?.originAirportCode,
    charterFareClass: charterFlightBundle?.fareClass,
  }
}

const UNASSIGNED = 'unassigned' as const

function findPlaceLocationInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripItineraryData['stops'][number]; day: TripDay | undefined } | undefined {
  if (!placeId || !itineraryData?.stops) return undefined

  for (const stop of itineraryData.stops) {
    const matchingDay = stop.days.find(day =>
      day.items.some(item => item.itemPlaceDetail?.placeId === placeId),
    )
    if (matchingDay) return { stop, day: matchingDay }

    if (stop.itemsWithoutDay?.some(item => item.itemPlaceDetail?.placeId === placeId)) {
      return { stop, day: undefined }
    }
  }

  return undefined
}

export function findPlaceDayInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): TripDay | typeof UNASSIGNED | undefined {
  const result = findPlaceLocationInItinerary(placeId, itineraryData)
  if (!result) return undefined
  return result.day ?? UNASSIGNED
}

export function isPlaceInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): boolean {
  return findPlaceDayInItinerary(placeId, itineraryData) !== undefined
}

export function getItineraryDayLabel(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): string | undefined {
  const result = findPlaceDayInItinerary(placeId, itineraryData)
  if (!result) return undefined
  if (result === UNASSIGNED) {
    const intl = getCurrentIntl()
    return intl.formatMessage({
      id: 'MgK5ISSCJKWklR8eg12e',
      defaultMessage: 'To be planned',
      description: 'Label for unassigned trip itinerary items that have no scheduled day',
    })
  }
  return getDayLabel(result.dayNumber, result.date)
}

interface StopWithCoordinates {
  stopPlaceDetail?: { lat?: number; lng?: number }
}

export function getStopCoordinates(
  stop: StopWithCoordinates | undefined,
): { lat: number; lng: number } | undefined {
  const lat = stop?.stopPlaceDetail?.lat
  const lng = stop?.stopPlaceDetail?.lng
  // The typeof pair narrows for the return; the helper decides NaN and out-of-range.
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined
  return hasValidLatLng(lat, lng) ? { lat, lng } : undefined
}

export function findStopById(
  stopId: number | undefined,
  itineraryData: TripItineraryData | undefined,
): TripStop | undefined {
  if (stopId === undefined) return undefined
  return itineraryData?.stops?.find((stop) => stop.id === stopId)
}

export function findPlaceStopInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripItineraryData['stops'][number]; dayNumber: number | undefined; dayDate: string | undefined } | undefined {
  const result = findPlaceLocationInItinerary(placeId, itineraryData)
  if (!result) return undefined
  return { stop: result.stop, dayNumber: result.day?.dayNumber, dayDate: result.day?.date }
}

interface OfferDayRange {
  label: string
  type: 'days' | 'to-be-planned'
  firstDayNumber?: number
  nightsCount?: number
}

export function getOfferItineraryDayRange(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): OfferDayRange | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  const matchingDays = itineraryData.stops
    .flatMap(stop => stop.days)
    .filter(day => day.items.some(hasOffer))
    .sort((a, b) => a.dayNumber - b.dayNumber)

  if (matchingDays.length > 0) {
    const first = matchingDays[0]
    const last = matchingDays[matchingDays.length - 1]

    let label: string
    if (first.date && last.date) {
      label = first.date === last.date
        ? getDayLabel(first.dayNumber, first.date)
        : formatShortDateRange({ startDate: moment(first.date), endDate: moment(last.date), includeYear: false })
    } else {
      const intl = getCurrentIntl()
      label = first.dayNumber === last.dayNumber
        ? intl.formatMessage({
            id: 'u1wB26lOYLMgkxrwovRa',
            defaultMessage: 'Day {dayNumber}',
            description: 'Single day label for offer itinerary placement in trip planner',
          }, { dayNumber: first.dayNumber })
        : intl.formatMessage({
            id: 's1UGPnSR6WtdZhdKm6EX',
            defaultMessage: 'Day {firstDay}-{lastDay}',
            description: 'Day range label for offer itinerary placement in trip planner',
          }, { firstDay: first.dayNumber, lastDay: last.dayNumber })
    }

    return { label, type: 'days', firstDayNumber: first.dayNumber, nightsCount: Math.max(1, last.dayNumber - first.dayNumber) }
  }

  const isUnassigned = itineraryData.stops
    .some(stop => stop.itemsWithoutDay?.some(hasOffer) || hasOfferTransport(stop, offerId))

  if (isUnassigned) {
    const intl = getCurrentIntl()
    return {
      label: intl.formatMessage({
        id: 'nWHuKJnHZR1G5XFSRZXT',
        defaultMessage: 'To be planned',
        description: 'Label for unassigned trip itinerary offer items that have no scheduled day',
      }),
      type: 'to-be-planned',
    }
  }
  return undefined
}

export function findOfferStopInItinerary(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): TripStop | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  return itineraryData.stops.find(stop =>
    stop.days.some(day => day.items.some(hasOffer)) ||
    stop.itemsWithoutDay?.some(hasOffer),
  )
}

export function findOfferStopDetails(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripStop; dayNumber: number | undefined; dayDate: string | undefined } | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  for (const stop of itineraryData.stops) {
    const matchingDay = stop.days.find(day => day.items.some(hasOffer))
    if (matchingDay) return { stop, dayNumber: matchingDay.dayNumber, dayDate: matchingDay.date }

    if (stop.itemsWithoutDay?.some(hasOffer)) {
      return { stop, dayNumber: undefined, dayDate: undefined }
    }
  }

  return undefined
}

export function doesStayExceedTripDates(
  checkInDayNumber: number,
  nights: number,
  stop: TripStop,
): { exceeds: boolean; extraDays: number } {
  // Unknown duration (no dates, nights null) is unbounded — there is nothing to exceed.
  // Do not treat missing duration as 0 days, and do not fall back to TripDay row count.
  const duration = resolveStopDuration(stop)
  if (!duration) {
    return { exceeds: false, extraDays: 0 }
  }

  const checkoutDay = checkInDayNumber + nights
  const extraDays = Math.max(0, checkoutDay - duration.dayCount)
  return { exceeds: extraDays > 0, extraDays }
}

export interface Coordinates {
  lat: number
  lng: number
}

const EARTH_RADIUS_KM = 6371

function haversineDistanceKm(a: Coordinates, b: Coordinates): number {
  const toRad = (deg: number) => deg * (Math.PI / 180)
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h))
}

const DEFAULT_MAX_DISTANCE_KM = 50

export function findNearestStop(
  coordinates: Coordinates | undefined,
  stops: ReadonlyArray<TripStop>,
  maxDistanceKm: number = DEFAULT_MAX_DISTANCE_KM,
): TripStop | undefined {
  if (!coordinates || stops.length === 0) return undefined

  let nearest: TripStop | undefined
  let nearestDistance = Infinity

  for (const stop of stops) {
    const stopCoords = stop.stopPlaceDetail
    if (!stopCoords) continue

    const distance = haversineDistanceKm(coordinates, stopCoords)
    if (distance <= maxDistanceKm && distance < nearestDistance) {
      nearest = stop
      nearestDistance = distance
    }
  }

  return nearest
}

function normaliseForPlaceMatch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
    .trim()
}

function containsPhrase(haystack: string, phrase: string): boolean {
  if (!phrase) return false
  return ` ${haystack} `.includes(` ${phrase} `)
}

function findUniqueStop(
  stops: ReadonlyArray<TripStop>,
  predicate: (stop: TripStop) => boolean,
): TripStop | undefined {
  const matches = stops.filter(predicate)
  return matches.length === 1 ? matches[0] : undefined
}

/**
 * Best-effort stop match for surfaces that only know a place as free text (no
 * coordinates or Google place id). Matches on the stop name first, then falls
 * back to the stop country, and only returns a stop when the match is unique.
 */
export function findStopByPlaceText(
  text: string | undefined,
  stops: ReadonlyArray<TripStop>,
): TripStop | undefined {
  if (!text || stops.length === 0) return undefined

  const normalisedText = normaliseForPlaceMatch(text)
  if (!normalisedText) return undefined

  return findUniqueStop(stops, stop => containsPhrase(normalisedText, normaliseForPlaceMatch(stop.placeName ?? '')))
    ?? findUniqueStop(stops, stop => containsPhrase(normalisedText, normaliseForPlaceMatch(stop.placeCountry ?? '')))
}
